import { useState, useEffect, useCallback, useMemo } from "react";
import { Loader2, Package, Calculator as CalcIcon, FolderOpen, LogOut, Save, History, ExternalLink, Lock, ShieldCheck, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";

const usd = (n) => (n === null || n === undefined || n === "" || isNaN(n)) ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso || "—"; } };

function LoginGate({ onLoggedIn }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role !== "partner" && u.role !== "admin") { toast.error("This account can't access the portal."); return; }
      onLoggedIn(u);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Invalid credentials"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <span className="text-white font-display font-extrabold text-2xl tracking-wide">ONEHARVEST</span>
          <span className="text-flame text-xs font-bold tracking-widest">PARTNER PORTAL</span>
        </div>
        <form onSubmit={submit} className="bg-charcoal border border-white/10 p-8" data-testid="portal-login-form">
          <div className="flex items-center gap-2 text-flame mb-6"><Lock className="h-4 w-4" /><span className="kicker">Partner Access</span></div>
          <label className="block text-sm text-white/60 mb-1.5">Email</label>
          <input data-testid="portal-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full bg-ink border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none mb-4" />
          <label className="block text-sm text-white/60 mb-1.5">Password</label>
          <input data-testid="portal-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-ink border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none mb-6" />
          <button data-testid="portal-login-btn" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-flame text-white py-3 font-semibold hover:bg-flame/90 transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Sign In
          </button>
        </form>
        <p className="text-center text-white/30 text-xs mt-6">Authorized distributors only. Contact OneHarvest for access.</p>
      </div>
    </div>
  );
}

function PricingRow({ p, onSaved }) {
  const [form, setForm] = useState({
    list_price: p.list_price ?? "", distributor_price: p.distributor_price ?? "",
    cost: p.cost ?? "", unit: p.unit ?? "", notes: p.notes ?? "",
  });
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(() => (
    String(form.list_price) !== String(p.list_price ?? "") || String(form.distributor_price) !== String(p.distributor_price ?? "") ||
    String(form.cost) !== String(p.cost ?? "") || form.unit !== (p.unit ?? "") || form.notes !== (p.notes ?? "")
  ), [form, p]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      ["list_price", "distributor_price", "cost"].forEach((k) => { if (form[k] !== "") body[k] = Number(form[k]); });
      body.unit = form.unit; body.notes = form.notes;
      await api.put(`/partner/pricing/${p.slug}`, body);
      toast.success(`${p.name} pricing saved`);
      onSaved();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const num = (k, ph) => (
    <input data-testid={`price-${k}-${p.slug}`} type="number" step="0.01" placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      className="w-full bg-ink border border-white/15 px-2.5 py-2 text-white text-sm focus:border-flame focus:outline-none" />
  );

  return (
    <div className="bg-ink border border-white/10 p-5" data-testid={`pricing-card-${p.slug}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-flame/40 text-flame">{p.category}</span>
          <h3 className="mt-1.5 font-display font-bold text-lg text-white">{p.name}</h3>
        </div>
        {p.updated_by && <div className="text-[10px] text-white/40 text-right">Updated {fmt(p.updated_at)}<br />by {p.updated_by}</div>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1">List Price</label>{num("list_price", "0.00")}</div>
        <div><label className="block text-[10px] uppercase tracking-widest text-flame mb-1">Distributor Price</label>{num("distributor_price", "0.00")}</div>
        <div><label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1">Your Cost</label>{num("cost", "0.00")}</div>
        <div><label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1">Unit</label>
          <input data-testid={`price-unit-${p.slug}`} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full bg-ink border border-white/15 px-2.5 py-2 text-white text-sm focus:border-flame focus:outline-none" /></div>
      </div>
      <div className="mt-3">
        <input data-testid={`price-notes-${p.slug}`} placeholder="Notes (MOQ, terms, freight…)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-ink border border-white/15 px-2.5 py-2 text-white text-sm focus:border-flame focus:outline-none" />
      </div>
      <div className="mt-3 flex justify-end">
        <button data-testid={`save-price-${p.slug}`} onClick={save} disabled={!dirty || saving} className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors disabled:opacity-40">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
      </div>
    </div>
  );
}

function ProductsPricing({ products, reload }) {
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  useEffect(() => { if (showHistory) api.get("/partner/pricing/history").then((r) => setHistory(r.data)).catch(() => {}); }, [showHistory]);
  return (
    <div data-testid="products-pricing">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-white/50">Set pricing per product. Distributor price feeds the ROI calculators.</p>
        <button data-testid="toggle-history" onClick={() => setShowHistory((s) => !s)} className="inline-flex items-center gap-1.5 text-xs text-flame hover:underline"><History className="h-3.5 w-3.5" /> {showHistory ? "Hide" : "Change"} history</button>
      </div>
      {showHistory ? (
        <div className="bg-ink border border-white/10 divide-y divide-white/5" data-testid="pricing-history">
          {history.length === 0 && <div className="p-6 text-white/40 text-sm">No changes recorded yet.</div>}
          {history.map((h, i) => (
            <div key={i} className="p-4 text-sm">
              <div className="flex justify-between"><span className="text-white font-semibold">{h.product}</span><span className="text-white/40 text-xs">{fmt(h.at)} · {h.actor}</span></div>
              <div className="mt-1 text-white/60 text-xs space-x-3">
                {Object.entries(h.changes).map(([k, v]) => <span key={k}>{k}: <span className="text-white/40">{String(v.from ?? "—")}</span> → <span className="text-flame">{String(v.to ?? "—")}</span></span>)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-4">{products.map((p) => <PricingRow key={p.slug} p={p} onSaved={reload} />)}</div>
      )}
    </div>
  );
}

function PortalCalculator({ products }) {
  const bbm = products.find((p) => p.slug === "big-bad-mats");
  const dp = bbm?.distributor_price;
  const [mats, setMats] = useState(500);
  const [deployments, setDeployments] = useState(6);
  const [altPerUse, setAltPerUse] = useState(45);

  const r = useMemo(() => {
    if (!dp) return null;
    const oneharvest = mats * dp;              // reusable, one-time purchase
    const traditional = mats * altPerUse * deployments; // per-use spend over project
    const savings = traditional - oneharvest;
    const roi = oneharvest ? Math.round((savings / oneharvest) * 100) : 0;
    return { oneharvest, traditional, savings, roi };
  }, [dp, mats, deployments, altPerUse]);

  return (
    <div data-testid="portal-calculator" className="max-w-3xl">
      <p className="text-sm text-white/50 mb-5">Ground-protection ROI, powered by your saved <span className="text-flame">Big Bad Mats distributor price</span>.</p>
      {!dp ? (
        <div className="bg-ink border border-amber-500/30 p-6 text-amber-200/80 text-sm" data-testid="calc-no-price">
          Enter a distributor price for <strong>Big Bad Mats</strong> in the Products &amp; Pricing tab to activate this calculator.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-ink border border-white/10 p-6 space-y-4">
            <div className="text-xs text-white/40">Distributor price in use: <span className="text-flame font-semibold">{usd(dp)}</span> {bbm.unit}</div>
            {[["Mats deployed", mats, setMats], ["Deployments over project", deployments, setDeployments], ["Traditional cost per deployment ($/mat)", altPerUse, setAltPerUse]].map(([label, val, set]) => (
              <div key={label}>
                <label className="block text-xs text-white/50 mb-1.5">{label}</label>
                <input type="number" value={val} onChange={(e) => set(Number(e.target.value))} className="w-full bg-charcoal border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none" data-testid={`calc-input-${label.split(" ")[0].toLowerCase()}`} />
              </div>
            ))}
          </div>
          <div className="bg-gradient-to-br from-flame/15 to-ink border border-flame/30 p-6 flex flex-col justify-center" data-testid="calc-results">
            <div className="text-xs uppercase tracking-widest text-white/50">Projected Savings</div>
            <div className="font-display font-black text-4xl text-white mt-1">{usd(r.savings)}</div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between text-white/70"><span>OneHarvest (reusable)</span><span>{usd(r.oneharvest)}</span></div>
              <div className="flex justify-between text-white/70"><span>Traditional (per-use)</span><span>{usd(r.traditional)}</span></div>
              <div className="flex justify-between text-flame font-semibold border-t border-white/10 pt-2"><span>ROI</span><span>{r.roi}%</span></div>
            </div>
          </div>
        </div>
      )}
      <p className="mt-5 text-[11px] text-white/30">Illustrative estimate for evaluation only — not a quote or guarantee. Actual outcomes vary by site conditions &amp; usage.</p>
    </div>
  );
}

function Materials() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/partner/materials").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (items === null) return <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div>;
  if (items.length === 0) return (
    <div className="text-center text-white/40 py-16 border border-dashed border-white/10" data-testid="materials-empty">
      <FolderOpen className="h-8 w-8 mx-auto mb-3 text-white/20" />
      Marketing materials are coming soon. New co-branded assets will appear here as they're published.
    </div>
  );
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="materials-grid">
      {items.map((m) => (
        <a key={m.id} href={m.url} target="_blank" rel="noreferrer" className="group bg-ink border border-white/10 p-5 hover:border-flame/50 transition-colors">
          <span className="text-[9px] uppercase tracking-widest text-flame">{m.category}</span>
          <h3 className="mt-1.5 font-display font-bold text-white group-hover:text-flame transition-colors">{m.title}</h3>
          <p className="mt-1 text-sm text-white/50">{m.description}</p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-flame text-sm">Open <ExternalLink className="h-3.5 w-3.5" /></span>
        </a>
      ))}
    </div>
  );
}

export default function PartnerPortal() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("pricing");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(async () => {
    try { const { data } = await api.get("/partner/products"); setProducts(data); }
    catch (e) { /* handled by gate */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user && (user.role === "partner" || user.role === "admin")) loadProducts();
  }, [user, loadProducts]);

  if (user === null) return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div>;
  if (!user || (user.role !== "partner" && user.role !== "admin")) return <LoginGate onLoggedIn={loadProducts} />;

  const TABS = [
    { k: "pricing", label: "Products & Pricing", icon: Package },
    { k: "calculator", label: "Calculators", icon: CalcIcon },
    { k: "materials", label: "Marketing Materials", icon: FolderOpen },
  ];

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="partner-portal">
      <header className="border-b border-white/10 bg-charcoal">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-display font-extrabold tracking-wide">ONEHARVEST</span>
            <span className="text-flame text-[10px] font-bold tracking-widest">PARTNER PORTAL</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/50 hidden sm:inline" data-testid="portal-user">{user.company_name || user.name || user.email}</span>
            <button onClick={logout} data-testid="portal-logout" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-flame transition-colors"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 py-8">
        <div className="flex items-center gap-1 mb-8 border-b border-white/10">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`portal-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.k ? "border-flame text-flame" : "border-transparent text-white/50 hover:text-white"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div> : (
          <>
            {tab === "pricing" && <ProductsPricing products={products} reload={loadProducts} />}
            {tab === "calculator" && <PortalCalculator products={products} />}
            {tab === "materials" && <Materials />}
          </>
        )}
      </div>
    </div>
  );
}
